import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    alpha,
} from '@mui/material';
import {
    MenuBook as HandbookIcon,
    Home as OverviewIcon,
    FilterList as FilterIcon,
    History as HistoryIcon,
    AssignmentInd as CategoryRequestIcon,
    EventAvailable as PersonalForecastIcon,
    DateRange as YearSummaryIcon,
    Email as MailboxIcon,
    FlightTakeoff as AbsentIcon,
    Sync as ChangeRequestIcon,
    Person as ProfileIcon,
    Shield as AdminIcon,
    EventNote as ScheduleIcon,
    ConfirmationNumber as TemplateSetIcon,
    AssignmentTurnedIn as ReviewRequestIcon,
    AddCircle as AddEventIcon,
    Brightness4 as ThemeIcon,
    Category as CategoryIcon,
    People as PeopleIcon,
    BarChart as MetricsIcon,
} from '@mui/icons-material';
import BaseModal from '@/components/modals/BaseModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { close } from '@/store/uiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import { useGetUserInfoQuery, useGetSettingsQuery } from '@/store/apiSlice';

// ── Section IDs ───────────────────────────────────────────────────────────────
type SectionId =
    | 'overview'
    | 'events'
    | 'absent'
    | 'filter'
    | 'history'
    | 'forecast'
    | 'profile'
    | 'theme'
    | 'yearSummary'
    | 'personalForecast'
    | 'changeRequests'
    | 'categoryMemberships'
    | 'mailbox'
    | 'proxy'
    | 'responsible'
    | 'scheduleViewing'
    | 'scheduleEditing'
    | 'scheduleTemplates'
    | 'adminUsers'
    | 'adminCategories'
    | 'adminEvents'
    | 'adminMetrics'
    | 'adminLogs';

interface Section {
    id: SectionId;
    icon: React.ReactNode;
    adminOnly?: boolean;
    scheduleEditorOnly?: boolean;
    scheduleTemplateEditorOnly?: boolean;
    responsibleOnly?: boolean;
    proxyOnly?: boolean;
    mailOnly?: boolean;
    scheduleViewOnly?: boolean;
}

// ── Reusable sub-components ───────────────────────────────────────────────────
const SectionTitle = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="h6" fontWeight={700}>
            {label}
        </Typography>
    </Stack>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
        {children}
    </Typography>
);

const Tip = ({ children }: { children: React.ReactNode }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                mt: 1,
                mb: 1.5,
                px: 2,
                py: 1,
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                bgcolor: alpha(theme.palette.primary.main, 0.07),
                borderRadius: '0 6px 6px 0',
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
};

const RoleBadge = ({
    label,
    color,
}: {
    label: string;
    color: 'warning' | 'error' | 'info' | 'success';
}) => <Chip label={label} color={color} size="small" sx={{ mb: 1.5 }} />;

// ── Main component ────────────────────────────────────────────────────────────
const HandbookModal = () => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.ui.open.handbook);
    const onClose = () => dispatch(close('handbook'));

    const { data: userInfo } = useGetUserInfoQuery();
    const { data: settings } = useGetSettingsQuery();

    const isAdmin = !!userInfo?.admin;
    const isScheduleEditor = !!(userInfo?.scheduleEditor || userInfo?.admin);
    const isScheduleTemplateEditor = !!(userInfo?.scheduleTemplateEditor || userInfo?.admin);
    const isResponsible = (userInfo?.categoriesResponsible?.length ?? 0) > 0;
    const isProxy = (userInfo?.represents?.length ?? 0) > 0;
    const mailEnabled = !!settings?.mail?.enabled;
    const scheduleEnabled = !!settings?.schedule?.enabled;

    const [activeSection, setActiveSection] = useState<SectionId>('overview');

    const allSections: Section[] = [
        { id: 'overview', icon: <OverviewIcon /> },
        { id: 'events', icon: <OverviewIcon /> },
        { id: 'absent', icon: <AbsentIcon /> },
        { id: 'filter', icon: <FilterIcon /> },
        { id: 'history', icon: <HistoryIcon /> },
        { id: 'forecast', icon: <PersonalForecastIcon /> },
        { id: 'profile', icon: <ProfileIcon /> },
        { id: 'theme', icon: <ThemeIcon /> },
        { id: 'yearSummary', icon: <YearSummaryIcon /> },
        { id: 'personalForecast', icon: <PersonalForecastIcon /> },
        { id: 'changeRequests', icon: <ChangeRequestIcon /> },
        { id: 'categoryMemberships', icon: <CategoryRequestIcon /> },
        { id: 'mailbox', icon: <MailboxIcon />, mailOnly: true },
        { id: 'proxy', icon: <ProfileIcon />, proxyOnly: true },
        { id: 'responsible', icon: <ReviewRequestIcon />, responsibleOnly: true },
        { id: 'scheduleViewing', icon: <ScheduleIcon />, scheduleViewOnly: true },
        { id: 'scheduleEditing', icon: <ScheduleIcon />, scheduleEditorOnly: true },
        { id: 'scheduleTemplates', icon: <TemplateSetIcon />, scheduleTemplateEditorOnly: true },
        { id: 'adminUsers', icon: <PeopleIcon />, adminOnly: true },
        { id: 'adminCategories', icon: <CategoryIcon />, adminOnly: true },
        { id: 'adminEvents', icon: <AddEventIcon />, adminOnly: true },
        { id: 'adminMetrics', icon: <MetricsIcon />, adminOnly: true },
        { id: 'adminLogs', icon: <AdminIcon />, adminOnly: true },
    ];

    const visibleSections = allSections.filter((s) => {
        if (s.adminOnly && !isAdmin) return false;
        if (s.scheduleEditorOnly && !isScheduleEditor) return false;
        if (s.scheduleTemplateEditorOnly && !isScheduleTemplateEditor) return false;
        if (s.responsibleOnly && !isResponsible) return false;
        if (s.proxyOnly && !isProxy) return false;
        if (s.mailOnly && !mailEnabled) return false;
        if (s.scheduleViewOnly && !scheduleEnabled) return false;
        return true;
    });

    const hb = LL.handbook;

    const renderSection = (id: SectionId) => {
        switch (id) {
            case 'overview':
                return (
                    <>
                        <SectionTitle icon={<HandbookIcon />} label={hb.sections.overview()} />
                        <Paragraph>{hb.overview.intro()}</Paragraph>
                        <Paragraph>{hb.overview.navigation()}</Paragraph>
                        <Tip>{hb.overview.tip()}</Tip>
                    </>
                );
            case 'events':
                return (
                    <>
                        <SectionTitle icon={<OverviewIcon />} label={hb.sections.events()} />
                        <Paragraph>{hb.events.what()}</Paragraph>
                        <Paragraph>{hb.events.expand()}</Paragraph>
                        <Paragraph>{hb.events.categories()}</Paragraph>
                        <Paragraph>{hb.events.canceled()}</Paragraph>
                        <Tip>{hb.events.tip()}</Tip>
                    </>
                );
            case 'absent':
                return (
                    <>
                        <SectionTitle icon={<AbsentIcon />} label={hb.sections.absent()} />
                        <Paragraph>{hb.absent.what()}</Paragraph>
                        <Paragraph>{hb.absent.how()}</Paragraph>
                        <Paragraph>{hb.absent.restriction()}</Paragraph>
                        <Tip>{hb.absent.tip()}</Tip>
                    </>
                );
            case 'filter':
                return (
                    <>
                        <SectionTitle icon={<FilterIcon />} label={hb.sections.filter()} />
                        <Paragraph>{hb.filter.what()}</Paragraph>
                        <Paragraph>{hb.filter.how()}</Paragraph>
                        <Paragraph>{hb.filter.assigned()}</Paragraph>
                        <Tip>{hb.filter.tip()}</Tip>
                    </>
                );
            case 'history':
                return (
                    <>
                        <SectionTitle icon={<HistoryIcon />} label={hb.sections.history()} />
                        <Paragraph>{hb.history.what()}</Paragraph>
                        <Paragraph>{hb.history.how()}</Paragraph>
                        <Tip>{hb.history.tip()}</Tip>
                    </>
                );
            case 'forecast':
                return (
                    <>
                        <SectionTitle
                            icon={<PersonalForecastIcon />}
                            label={hb.sections.forecast()}
                        />
                        <Paragraph>{hb.forecast.what()}</Paragraph>
                        <Paragraph>{hb.forecast.slider()}</Paragraph>
                        <Tip>{hb.forecast.tip()}</Tip>
                    </>
                );
            case 'profile':
                return (
                    <>
                        <SectionTitle icon={<ProfileIcon />} label={hb.sections.profile()} />
                        <Paragraph>{hb.profile.what()}</Paragraph>
                        <Paragraph>{hb.profile.email()}</Paragraph>
                        <Paragraph>{hb.profile.language()}</Paragraph>
                        <Paragraph>{hb.profile.preselect()}</Paragraph>
                        <Paragraph>{hb.profile.autoExpand()}</Paragraph>
                        <Paragraph>{hb.profile.reminders()}</Paragraph>
                        <Paragraph>{hb.profile.notifyAbsent()}</Paragraph>
                        <Tip>{hb.profile.tip()}</Tip>
                    </>
                );
            case 'theme':
                return (
                    <>
                        <SectionTitle icon={<ThemeIcon />} label={hb.sections.theme()} />
                        <Paragraph>{hb.theme.what()}</Paragraph>
                        <Paragraph>{hb.theme.how()}</Paragraph>
                    </>
                );
            case 'yearSummary':
                return (
                    <>
                        <SectionTitle
                            icon={<YearSummaryIcon />}
                            label={hb.sections.yearSummary()}
                        />
                        <Paragraph>{hb.yearSummary.what()}</Paragraph>
                        <Paragraph>{hb.yearSummary.how()}</Paragraph>
                        <Tip>{hb.yearSummary.tip()}</Tip>
                    </>
                );
            case 'personalForecast':
                return (
                    <>
                        <SectionTitle
                            icon={<PersonalForecastIcon />}
                            label={hb.sections.personalForecast()}
                        />
                        <Paragraph>{hb.personalForecast.what()}</Paragraph>
                        <Paragraph>{hb.personalForecast.changeRequest()}</Paragraph>
                        <Paragraph>{hb.personalForecast.cancelRequest()}</Paragraph>
                        <Tip>{hb.personalForecast.tip()}</Tip>
                    </>
                );
            case 'changeRequests':
                return (
                    <>
                        <SectionTitle
                            icon={<ChangeRequestIcon />}
                            label={hb.sections.changeRequests()}
                        />
                        <Paragraph>{hb.changeRequests.what()}</Paragraph>
                        <Paragraph>{hb.changeRequests.how()}</Paragraph>
                        <Paragraph>{hb.changeRequests.outcome()}</Paragraph>
                        <Tip>{hb.changeRequests.tip()}</Tip>
                    </>
                );
            case 'categoryMemberships':
                return (
                    <>
                        <SectionTitle
                            icon={<CategoryRequestIcon />}
                            label={hb.sections.categoryMemberships()}
                        />
                        <Paragraph>{hb.categoryMemberships.what()}</Paragraph>
                        <Paragraph>{hb.categoryMemberships.join()}</Paragraph>
                        <Paragraph>{hb.categoryMemberships.leave()}</Paragraph>
                        <Paragraph>{hb.categoryMemberships.pending()}</Paragraph>
                        <Tip>{hb.categoryMemberships.tip()}</Tip>
                    </>
                );
            case 'mailbox':
                return (
                    <>
                        <SectionTitle icon={<MailboxIcon />} label={hb.sections.mailbox()} />
                        <Paragraph>{hb.mailbox.what()}</Paragraph>
                        <Paragraph>{hb.mailbox.reminders()}</Paragraph>
                        <Tip>{hb.mailbox.tip()}</Tip>
                    </>
                );
            case 'proxy':
                return (
                    <>
                        <SectionTitle icon={<ProfileIcon />} label={hb.sections.proxy()} />
                        <RoleBadge label={hb.badges.proxy()} color="success" />
                        <Paragraph>{hb.proxy.what()}</Paragraph>
                        <Paragraph>{hb.proxy.sees()}</Paragraph>
                        <Paragraph>{hb.proxy.cando()}</Paragraph>
                        <Paragraph>{hb.proxy.cantdo()}</Paragraph>
                        <Tip>{hb.proxy.tip()}</Tip>
                    </>
                );
            case 'responsible':
                return (
                    <>
                        <SectionTitle
                            icon={<ReviewRequestIcon />}
                            label={hb.sections.responsible()}
                        />
                        <RoleBadge label={hb.badges.responsible()} color="info" />
                        <Paragraph>{hb.responsible.what()}</Paragraph>
                        <Paragraph>{hb.responsible.review()}</Paragraph>
                        <Paragraph>{hb.responsible.badge()}</Paragraph>
                        <Tip>{hb.responsible.tip()}</Tip>
                    </>
                );
            case 'scheduleViewing':
                return (
                    <>
                        <SectionTitle
                            icon={<ScheduleIcon />}
                            label={hb.sections.scheduleViewing()}
                        />
                        <Paragraph>{hb.scheduleViewing.what()}</Paragraph>
                        <Paragraph>{hb.scheduleViewing.open()}</Paragraph>
                        <Paragraph>{hb.scheduleViewing.pdf()}</Paragraph>
                        <Tip>{hb.scheduleViewing.tip()}</Tip>
                    </>
                );
            case 'scheduleEditing':
                return (
                    <>
                        <SectionTitle
                            icon={<ScheduleIcon />}
                            label={hb.sections.scheduleEditing()}
                        />
                        <RoleBadge label={hb.badges.scheduleEditor()} color="error" />
                        <Paragraph>{hb.scheduleEditing.what()}</Paragraph>
                        <Paragraph>{hb.scheduleEditing.add()}</Paragraph>
                        <Paragraph>{hb.scheduleEditing.template()}</Paragraph>
                        <Paragraph>{hb.scheduleEditing.reorder()}</Paragraph>
                        <Paragraph>{hb.scheduleEditing.copy()}</Paragraph>
                        <Paragraph>{hb.scheduleEditing.pdf()}</Paragraph>
                        <Tip>{hb.scheduleEditing.tip()}</Tip>
                    </>
                );
            case 'scheduleTemplates':
                return (
                    <>
                        <SectionTitle
                            icon={<TemplateSetIcon />}
                            label={hb.sections.scheduleTemplates()}
                        />
                        <RoleBadge label={hb.badges.scheduleTemplateEditor()} color="error" />
                        <Paragraph>{hb.scheduleTemplates.what()}</Paragraph>
                        <Paragraph>{hb.scheduleTemplates.templates()}</Paragraph>
                        <Paragraph>{hb.scheduleTemplates.sets()}</Paragraph>
                        <Paragraph>{hb.scheduleTemplates.apply()}</Paragraph>
                        <Tip>{hb.scheduleTemplates.tip()}</Tip>
                    </>
                );
            case 'adminUsers':
                return (
                    <>
                        <SectionTitle icon={<PeopleIcon />} label={hb.sections.adminUsers()} />
                        <RoleBadge label={hb.badges.admin()} color="warning" />
                        <Paragraph>{hb.adminUsers.what()}</Paragraph>
                        <Paragraph>{hb.adminUsers.roles()}</Paragraph>
                        <Paragraph>{hb.adminUsers.categories()}</Paragraph>
                        <Paragraph>{hb.adminUsers.proxy()}</Paragraph>
                        <Paragraph>{hb.adminUsers.authCode()}</Paragraph>
                        <Tip>{hb.adminUsers.tip()}</Tip>
                    </>
                );
            case 'adminCategories':
                return (
                    <>
                        <SectionTitle
                            icon={<CategoryIcon />}
                            label={hb.sections.adminCategories()}
                        />
                        <RoleBadge label={hb.badges.admin()} color="warning" />
                        <Paragraph>{hb.adminCategories.what()}</Paragraph>
                        <Paragraph>{hb.adminCategories.settings()}</Paragraph>
                        <Paragraph>{hb.adminCategories.responsible()}</Paragraph>
                        <Paragraph>{hb.adminCategories.exclusions()}</Paragraph>
                        <Tip>{hb.adminCategories.tip()}</Tip>
                    </>
                );
            case 'adminEvents':
                return (
                    <>
                        <SectionTitle icon={<AddEventIcon />} label={hb.sections.adminEvents()} />
                        <RoleBadge label={hb.badges.admin()} color="warning" />
                        <Paragraph>{hb.adminEvents.what()}</Paragraph>
                        <Paragraph>{hb.adminEvents.recurrence()}</Paragraph>
                        <Paragraph>{hb.adminEvents.cancel()}</Paragraph>
                        <Tip>{hb.adminEvents.tip()}</Tip>
                    </>
                );
            case 'adminMetrics':
                return (
                    <>
                        <SectionTitle icon={<MetricsIcon />} label={hb.sections.adminMetrics()} />
                        <RoleBadge label={hb.badges.admin()} color="warning" />
                        <Paragraph>{hb.adminMetrics.what()}</Paragraph>
                        <Paragraph>{hb.adminMetrics.sections()}</Paragraph>
                        <Paragraph>{hb.adminMetrics.filter()}</Paragraph>
                        <Paragraph>{hb.adminMetrics.range()}</Paragraph>
                        <Tip>{hb.adminMetrics.tip()}</Tip>
                    </>
                );
            case 'adminLogs':
                return (
                    <>
                        <SectionTitle icon={<AdminIcon />} label={hb.sections.adminLogs()} />
                        <RoleBadge label={hb.badges.admin()} color="warning" />
                        <Paragraph>{hb.adminLogs.what()}</Paragraph>
                        <Paragraph>{hb.adminLogs.search()}</Paragraph>
                        <Tip>{hb.adminLogs.tip()}</Tip>
                    </>
                );
            default:
                return null;
        }
    };

    const currentSectionObj = visibleSections.find((s) => s.id === activeSection);
    // If active section is no longer visible (role changed), fall back to overview
    const safeActive: SectionId = currentSectionObj ? activeSection : 'overview';

    // Group sections for the sidebar
    const generalSections: SectionId[] = [
        'overview',
        'events',
        'absent',
        'filter',
        'history',
        'forecast',
        'profile',
        'theme',
        'yearSummary',
        'personalForecast',
        'changeRequests',
        'categoryMemberships',
    ];
    const extendedSections: SectionId[] = ['mailbox', 'proxy', 'responsible'];
    const scheduleSections: SectionId[] = [
        'scheduleViewing',
        'scheduleEditing',
        'scheduleTemplates',
    ];
    const adminSections: SectionId[] = [
        'adminUsers',
        'adminCategories',
        'adminEvents',
        'adminMetrics',
        'adminLogs',
    ];

    const renderGroup = (ids: SectionId[], groupLabel: string) => {
        const items = visibleSections.filter((s) => ids.includes(s.id));
        if (items.length === 0) return null;
        return (
            <>
                <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{
                        px: 2,
                        pt: 1.5,
                        pb: 0.5,
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                    }}
                >
                    {groupLabel}
                </Typography>
                {items.map((s) => (
                    <ListItem key={s.id} disablePadding>
                        <ListItemButton
                            selected={safeActive === s.id}
                            onClick={() => setActiveSection(s.id)}
                            sx={{
                                borderRadius: 1,
                                mx: 0.5,
                                '&.Mui-selected': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: 'primary.main',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    mr: 1.5,
                                    display: 'flex',
                                    color: safeActive === s.id ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {s.icon}
                            </Box>
                            <ListItemText
                                primary={hb.sections[s.id]()}
                                slotProps={{
                                    primary: {
                                        variant: 'body2',
                                        fontWeight: safeActive === s.id ? 700 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </>
        );
    };

    const sidebar = (
        <Box
            sx={{
                width: isMobile ? '100%' : 220,
                flexShrink: 0,
                borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                overflowY: 'auto',
                height: isMobile ? 'auto' : '100%',
            }}
        >
            <List dense disablePadding>
                {renderGroup(generalSections, hb.groups.general())}
                {renderGroup(extendedSections, hb.groups.extended())}
                {scheduleEnabled && renderGroup(scheduleSections, hb.groups.schedule())}
                {isAdmin && renderGroup(adminSections, hb.groups.admin())}
            </List>
        </Box>
    );

    const content = (
        <Box
            sx={{
                flex: 1,
                px: { xs: 0, md: 3 },
                pt: isMobile ? 2 : 0,
                minWidth: 0,
                overflowY: 'auto',
                height: isMobile ? 'auto' : '100%',
            }}
        >
            {renderSection(safeActive)}
        </Box>
    );

    return (
        <BaseModal
            open={isOpen}
            onClose={onClose}
            maxWidth="lg"
            centeredHeader={{
                content: (
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <HandbookIcon sx={{ color: 'white', fontSize: 28 }} />
                        <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                            {hb.title()}
                        </Typography>
                    </Stack>
                ),
            }}
            actions={
                <Button variant="contained" onClick={onClose}>
                    {LL.common.close()}
                </Button>
            }
            minHeight={{ xs: 'auto', sm: '75vh' }}
        >
            {isMobile ? (
                // Mobile: sidebar capped height + scrollable, content scrollable below
                <Stack sx={{ gap: 0, height: '100%' }}>
                    <Box
                        sx={{
                            overflowY: 'auto',
                            maxHeight: '25vh',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            pb: 1,
                            flexShrink: 0,
                        }}
                    >
                        {sidebar}
                    </Box>
                    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>{content}</Box>
                </Stack>
            ) : (
                // Desktop: fixed-height row — sidebar and content each scroll independently
                <Stack
                    direction="row"
                    sx={{
                        height: '60vh',
                        gap: 0,
                        overflow: 'hidden',
                    }}
                >
                    {sidebar}
                    <Box sx={{ flex: 1, p: 3, overflowY: 'auto', height: '100%' }}>{content}</Box>
                </Stack>
            )}
        </BaseModal>
    );
};

export default HandbookModal;
